import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Copy, Check, FileDown, Plus, Loader2, Mail,
  Pencil, CalendarPlus, Eye, Send,
} from "lucide-react";

/**
 * Admin composer for SideB Agent Agreements.
 *
 * Mirrors the quote-composer feature set: draft/preview flow, PATCH edit,
 * one-click Extend +14d, status pill, warning banner on sent edit.
 */
export default function AdminAgentAgreements() {
  const { user, loading } = useAuth();
  const [agreements, setAgreements] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAgreement, setEditingAgreement] = useState(null);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/admin/agent-agreements");
      setAgreements(data.agreements || []);
    } catch {
      // silent — the table will just show empty; keeps the UI usable
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user]);

  const handleDialogOpenChange = (next) => {
    setDialogOpen(next);
    if (!next) setEditingAgreement(null);
  };

  const startEdit = (row) => {
    setEditingAgreement(row);
    setDialogOpen(true);
  };

  const onUpdated = (updated) => {
    setAgreements((prev) => prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)));
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  }
  if (!user) return <Navigate to="/login?return_to=%2Fadmin%2Fagent-agreements" replace />;
  if (user.role !== "admin") {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center">
        <h1 className="font-display text-3xl tracking-tight">Admin only</h1>
        <p className="text-muted-foreground mt-3">Your account doesn&apos;t have admin access.</p>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen" data-testid="admin-agent-agreements-page">
      <Helmet>
        <title>Agent Agreements · SideB Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.16em] text-foreground/55 hover:text-[#ac62ea] mb-8" data-testid="admin-agent-agreements-back">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Admin
        </Link>
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="label-mono">Admin · Agent program</div>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-[-0.03em] mt-3">Agent Agreements</h1>
            <p className="text-muted-foreground mt-3 max-w-2xl leading-relaxed">
              Draft a commission agreement, preview it, then send. Agents accept the mutual NDA before commercial
              terms are revealed, then sign to activate. Every link expires 14 days from creation by default.
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
            <DialogTrigger asChild>
              <Button
                className="rounded-full bg-[#ac62ea] hover:bg-[#9851d6] text-white h-10 px-5"
                onClick={() => setEditingAgreement(null)}
                data-testid="admin-agent-agreements-new-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> New agreement
              </Button>
            </DialogTrigger>
            <AgreementDialog
              open={dialogOpen}
              onOpenChange={handleDialogOpenChange}
              editingAgreement={editingAgreement}
              onCreated={load}
              onUpdated={onUpdated}
            />
          </Dialog>
        </div>

        <AgreementsTable
          rows={agreements}
          refreshing={refreshing}
          onRefresh={load}
          onEdit={startEdit}
          onUpdated={onUpdated}
        />
      </div>
    </div>
  );
}

/* -------------------------------- DIALOG --------------------------------- */

const EMPTY_FORM = {
  agent_name: "",
  agent_email: "",
  agent_org: "",
  commission: "15% of net collected revenue on covered services",
  payout_schedule: "Net 30 days after SideB receives client payment",
  services_covered: "",
  expires_at: "",
  notes: "",
};

function AgreementDialog({ open, onOpenChange, editingAgreement, onCreated, onUpdated }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const isEditing = !!editingAgreement;
  const isSentEdit = isEditing && editingAgreement?.status !== "draft" && editingAgreement?.email_sent_at;

  // Pre-fill on open in edit-mode; reset on close.
  useEffect(() => {
    if (!open) return;
    if (editingAgreement) {
      setForm({
        agent_name: editingAgreement.agent_name || "",
        agent_email: editingAgreement.agent_email || "",
        agent_org: editingAgreement.agent_org || "",
        commission: editingAgreement.commission || "",
        payout_schedule: editingAgreement.payout_schedule || "",
        services_covered: (editingAgreement.services_covered || []).join(", "),
        expires_at: editingAgreement.expires_at || "",
        notes: editingAgreement.notes || "",
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [open, editingAgreement]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (sendEmail) => {
    if (!form.agent_name.trim() || !form.agent_email.includes("@") || !form.commission.trim()
        || !form.payout_schedule.trim() || !form.services_covered.trim()) {
      toast.error("Name, email, commission, payout, and services covered are required.");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        agent_name: form.agent_name.trim(),
        agent_email: form.agent_email.trim().toLowerCase(),
        agent_org: form.agent_org.trim() || null,
        commission: form.commission.trim(),
        payout_schedule: form.payout_schedule.trim(),
        services_covered: form.services_covered.split(",").map((s) => s.trim()).filter(Boolean),
        expires_at: form.expires_at || null,
        notes: form.notes.trim() || null,
      };

      if (isEditing) {
        const { data } = await api.patch(`/admin/agent-agreements/${editingAgreement.id}`, body);
        if (sendEmail) {
          const { data: sendData } = await api.post(`/admin/agent-agreements/${editingAgreement.id}/send-email`);
          toast.success("Changes saved — agent email re-sent");
          onUpdated({ ...data, status: sendData.status, email_sent_at: sendData.sent_at });
        } else {
          toast.success("Changes saved");
          onUpdated(data);
        }
      } else {
        const { data } = await api.post("/admin/agent-agreements", { ...body, send_email: sendEmail });
        if (sendEmail) {
          toast.success("Agreement created — share link emailed to agent");
        } else {
          toast.success("Draft saved — opening preview in a new tab");
          try {
            const previewUrl = data.share_url || `/agent-agreement/${data.id}`;
            window.open(previewUrl, "_blank", "noopener,noreferrer");
          } catch { /* popup blocked */ }
        }
        onCreated();
      }
      onOpenChange(false);
      setForm(EMPTY_FORM);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isEditing ? "Failed to save changes" : "Failed to create agreement"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent
      className="max-w-2xl max-h-[90vh] overflow-y-auto"
      data-testid="admin-agent-agreements-dialog"
    >
      <DialogHeader>
        <DialogTitle>{isEditing ? "Edit agent agreement" : "New agent agreement"}</DialogTitle>
      </DialogHeader>
      {isSentEdit && (
        <div
          className="mt-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
          data-testid="agreement-edit-sent-warning"
        >
          <strong>Heads up:</strong> this agreement has already been sent to the agent. The share
          link + access token stay the same — anyone who has it will see the updated content on their next refresh.
        </div>
      )}
      <div className="grid gap-4 mt-2">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Agent name *" value={form.agent_name} onChange={update("agent_name")} testId="ag-name" required />
          <Field label="Agent email *" type="email" value={form.agent_email} onChange={update("agent_email")} testId="ag-email" required />
        </div>
        <Field label="Agent org (optional)" value={form.agent_org} onChange={update("agent_org")} testId="ag-org" />
        <Field label="Commission * (free text — % or flat fee, any structure)"
               value={form.commission} onChange={update("commission")} testId="ag-commission"
               placeholder='e.g. "15% of net collected revenue Y1, 10% renewals" or "$5,000 flat per referral"'
               required multiline />
        <Field label="Payout schedule *" value={form.payout_schedule} onChange={update("payout_schedule")} testId="ag-payout" required multiline />
        <Field label="Services covered * (comma-separated)" value={form.services_covered} onChange={update("services_covered")} testId="ag-services" required
               placeholder="Payments Optimization Sprint, Studio Retainer, Discovery Session" />
        <Field label="Link expires (optional, defaults to +14 days)" type="date" value={form.expires_at} onChange={update("expires_at")} testId="ag-expires" />
        <Field label="Additional notes (optional)" value={form.notes} onChange={update("notes")} testId="ag-notes" multiline />
      </div>
      <DialogFooter className="mt-4 gap-2">
        <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button
          variant="outline"
          onClick={() => submit(false)}
          disabled={submitting}
          data-testid="admin-agent-agreements-save-draft"
        >
          <Eye className="h-4 w-4 mr-1.5" />
          {submitting ? "Saving…" : isEditing ? "Save changes" : "Save as draft (preview)"}
        </Button>
        <Button
          onClick={() => submit(true)}
          disabled={submitting}
          className="bg-[#ac62ea] hover:bg-[#9851d6] text-white"
          data-testid="admin-agent-agreements-submit"
        >
          <Send className="h-4 w-4 mr-1.5" />
          {submitting
            ? "Sending…"
            : isEditing
            ? (isSentEdit ? "Save & re-send" : "Save & send now")
            : "Save & send now"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

const Field = ({ label, value, onChange, type = "text", testId, required, placeholder, multiline }) => (
  <label className="grid gap-1.5 text-sm">
    <span className="text-[11px] font-medium text-foreground/65 uppercase tracking-[0.12em]">{label}</span>
    {multiline ? (
      <textarea
        value={value} onChange={onChange} rows={3} placeholder={placeholder} required={required}
        className="rounded-lg border border-border bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#ac62ea]/40 focus:border-[#ac62ea] transition-colors"
        data-testid={`admin-agent-agreements-${testId}`}
      />
    ) : (
      <input
        type={type}
        value={value} onChange={onChange} placeholder={placeholder} required={required}
        className="rounded-lg border border-border bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#ac62ea]/40 focus:border-[#ac62ea] transition-colors"
        data-testid={`admin-agent-agreements-${testId}`}
      />
    )}
  </label>
);

/* ---------------------------------- TABLE --------------------------------- */

function AgreementsTable({ rows, refreshing, onRefresh, onEdit, onUpdated }) {
  const [copiedId, setCopiedId] = useState("");
  const [sendingId, setSendingId] = useState("");
  const [extendingId, setExtendingId] = useState("");
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const shareUrlFor = (row) =>
    `${window.location.origin}/agent-agreement/${row.id}?t=${encodeURIComponent(row.access_token || "")}`;

  const copyRowLink = async (row) => {
    try {
      await navigator.clipboard.writeText(shareUrlFor(row));
      setCopiedId(row.id);
      setTimeout(() => setCopiedId(""), 2000);
    } catch { /* silent */ }
  };

  const sendEmail = async (row) => {
    setSendingId(row.id);
    try {
      const { data } = await api.post(`/admin/agent-agreements/${row.id}/send-email`);
      toast.success(`Email sent to ${data.sent_to}`);
      onUpdated({ ...row, status: data.status, email_sent_at: data.sent_at, email_sent_count: (row.email_sent_count || 0) + 1 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send email. See admin notification failures.");
    } finally {
      setSendingId("");
    }
  };

  const extendExpiry = async (row) => {
    setExtendingId(row.id);
    try {
      const { data } = await api.post(`/admin/agent-agreements/${row.id}/extend-expiry`);
      toast.success(`Link extended — new expiry ${data.expires_at}`);
      onUpdated({ ...row, expires_at: data.expires_at, status: data.status });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not extend expiry.");
    } finally {
      setExtendingId("");
    }
  };

  return (
    <section className="mt-14" data-testid="admin-agent-agreements-table">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="label-mono">All agreements</div>
          <h2 className="font-display text-2xl font-semibold tracking-[-0.02em] mt-2">{rows.length} agreement{rows.length === 1 ? "" : "s"}</h2>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={refreshing} className="rounded-full">
          {refreshing ? "Refreshing…" : "Refresh"}
        </Button>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center text-sm text-muted-foreground">
          No agreements yet. Draft your first one above.
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#faf7ff] text-foreground/60">
              <tr className="text-left">
                <Th>Agent</Th>
                <Th>Commission</Th>
                <Th>Status</Th>
                <Th>Delivery</Th>
                <Th>Expires</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const expired = isExpired(row);
                const isDraft = row.status === "draft" || (!row.status && !row.email_sent_at);
                const isAccepted = row.status === "accepted";
                return (
                  <tr key={row.id} className="border-t border-border/60 hover:bg-[#faf7ff]/40" data-testid={`admin-agent-agreements-row-${row.id}`}>
                    <Td>
                      <div className="font-medium text-foreground">{row.agent_name}</div>
                      <div className="text-xs text-muted-foreground">{row.agent_email}{row.agent_org ? ` · ${row.agent_org}` : ""}</div>
                    </Td>
                    <Td>
                      <div className="text-sm text-foreground leading-snug">{row.commission || `${row.commission_pct || "—"}%`}</div>
                    </Td>
                    <Td><StatusPill status={row.status} row={row} /></Td>
                    <Td>
                      {row.signed_at ? (
                        new Date(row.signed_at).toLocaleDateString()
                      ) : row.email_sent_at ? (
                        <div className="text-xs">
                          <div className="text-emerald-700 font-medium">Emailed {new Date(row.email_sent_at).toLocaleDateString()}</div>
                          {row.email_sent_count > 1 ? <div className="text-muted-foreground">({row.email_sent_count} sends)</div> : null}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </Td>
                    <Td>
                      {row.expires_at ? (
                        <span className={`text-xs ${expired ? "text-rose-700 font-medium" : "text-foreground/70"}`}>
                          {row.expires_at}
                          {expired ? " · EXPIRED" : ""}
                        </span>
                      ) : <span className="text-muted-foreground">—</span>}
                    </Td>
                    <Td className="text-right">
                      <div className="inline-flex items-center gap-2 flex-wrap justify-end">
                        {expired && !isAccepted && (
                          <button
                            type="button"
                            onClick={() => extendExpiry(row)}
                            disabled={extendingId === row.id}
                            className="text-xs text-white bg-[#ac62ea] hover:bg-[#9851d6] rounded-full px-3 py-1 inline-flex items-center gap-1 disabled:opacity-40"
                            data-testid={`admin-agent-agreements-extend-${row.id}`}
                          >
                            <CalendarPlus className="h-3 w-3" /> {extendingId === row.id ? "Extending…" : "Extend +14d"}
                          </button>
                        )}
                        {!expired && !isAccepted && (
                          <button
                            type="button"
                            onClick={() => sendEmail(row)}
                            disabled={sendingId === row.id}
                            className="text-xs text-[#ac62ea] hover:underline inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid={`admin-agent-agreements-email-${row.id}`}
                          >
                            {sendingId === row.id ? (
                              <><Loader2 className="h-3 w-3 animate-spin" /> Sending…</>
                            ) : (
                              <><Mail className="h-3 w-3" /> {isDraft ? "Email now" : "Re-send"}</>
                            )}
                          </button>
                        )}
                        {!isAccepted && (
                          <button
                            type="button"
                            onClick={() => onEdit(row)}
                            className="text-xs text-[#ac62ea] hover:underline inline-flex items-center gap-1"
                            data-testid={`admin-agent-agreements-edit-${row.id}`}
                          >
                            <Pencil className="h-3 w-3" /> Edit
                          </button>
                        )}
                        <button type="button" onClick={() => copyRowLink(row)} className="text-xs text-[#ac62ea] hover:underline inline-flex items-center gap-1" data-testid={`admin-agent-agreements-copy-${row.id}`}>
                          {copiedId === row.id ? <><Check className="h-3 w-3" /> Copied</> : <><Copy className="h-3 w-3" /> Link</>}
                        </button>
                        {isAccepted && (
                          <a href={`${API}/agent-agreements/${row.id}/download.pdf?t=${encodeURIComponent(row.access_token || "")}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#ac62ea] hover:underline inline-flex items-center gap-1" data-testid={`admin-agent-agreements-pdf-${row.id}`}>
                            <FileDown className="h-3 w-3" /> PDF
                          </a>
                        )}
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 text-[11px] font-mono uppercase tracking-[0.12em] font-semibold ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-4 align-top ${className}`}>{children}</td>
);

const StatusPill = ({ status, row }) => {
  const raw = status || (row?.email_sent_at ? "sent" : "draft");
  const effective = row && isExpired(row) && raw !== "accepted" ? "expired" : raw;
  const map = {
    draft:    "bg-slate-100 text-slate-700 border-slate-200",
    sent:     "bg-amber-50 text-amber-800 border-amber-200",
    accepted: "bg-emerald-50 text-emerald-800 border-emerald-200",
    expired:  "bg-gray-100 text-gray-700 border-gray-200",
    revoked:  "bg-rose-50 text-rose-800 border-rose-200",
  };
  const cls = map[effective] || map.sent;
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-mono uppercase tracking-[0.12em] ${cls}`}
      data-testid={`admin-agent-agreements-status-${effective}`}
    >
      {effective}
    </span>
  );
};

function isExpired(row) {
  if (!row?.expires_at || row.status === "accepted") return false;
  // Treat expires_at as end-of-day
  try {
    const end = new Date(`${row.expires_at}T23:59:59Z`);
    return new Date() > end;
  } catch {
    return false;
  }
}
